import React, { useEffect, useState } from 'react';
import { Box, Divider, GlobalStyles, Grid, Typography } from '@mui/material';
import type { Column } from 'react-data-grid';
import MultiLevelMergedTable, {
	MultiLevelHeaderCell,
	MultiLevelTableColumn,
} from 'components/Widgets/MultiLevelMergedTable';
import PageWrapper from 'components/Common/PageWraper';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import EDFCataloging, {
	HeatmapRow,
	KpiItem,
} from './EDFCataloging';
import { useTheme } from '@mui/material/styles';
import { getEdfBenefitsTable, getEdfCatalogingTable, getEdfCostOverviewTable, getEdfHeatMapTable, getEdfPriorityMarketsTable } from 'service/service';
import LoaderComponent from 'components/Loader';

interface CostAllocationRow {
	id: number;
	category: string;
	subcategory: string;
	geographicalUsage: string;

	total2026: number | null;
	usBev2026: number | null;
	usFoods2026: number | null;
	latamFoods2026: number | null;
	emea2026: number | null;
	apacFoods2026: number | null;
	corporate2026: number | null;

	total2027: number | null;
	usBev2027: number | null;
	usFoods2027: number | null;
	latamFoods2027: number | null;
	emea2027: number | null;
	apacFoods2027: number | null;
	corporate2027: number | null;

	isSubtotal?: boolean;
	isTotal?: boolean;
}


interface DomainCoverageRow {
	id: number;
	domain: string;
	unitedStates: number | null;
	canada: number | null;
	unitedKingdom: number | null;
	france: number | null;
	netherlands: number | null;
	spain: number | null;
	russia: number | null;
	turkey: number | null;
	poland: number | null;
	mexico: number | null;
	brazil: number | null;
	southAfrica: number | null;
	egypt: number | null;
	india: number | null;
	saudiArabia: number | null;
	unitedArabEmirates: number | null;
	china: number | null;
	anz: number | null;
	total: number | null;
	isTotal?: boolean;
}

interface RetirementPlanRow {
	id: number;
	group: string;
	application: string;
	year2025: string;
	year2026: string;
	year2027: string;
	year2028: string;
	total: string;
	owner: string;
	status: string;
	isTotal?: boolean;
}

const HEADER_COLORS = {
	criteria: '#003C63',
	year2026: '#496A89',
	year2027: '#0879BA',
};

const formatCurrency = (
	value: CostAllocationRow[keyof CostAllocationRow],
) => {
	if (
		value === null ||
		value === undefined ||
		value === ''
	) {
		return '';
	}

	if (typeof value !== 'number') {
		return String(value);
	}

	return `$${value.toFixed(2)}`;
};


const renderTableValue = (
	value: string | number,
	fontWeight = 500,
) => (
	<RowTypography
		sx={{
			fontWeight,
		}}
	>
		{value}
	</RowTypography>
);

const renderMultilineTableValue = (
	value: string,
	fontWeight = 500,
) => (
	<RowTypography
		sx={{
			fontWeight,
			whiteSpace: 'normal',
			overflow: 'visible',
			textOverflow: 'unset',
			overflowWrap: 'break-word',
			wordBreak: 'break-word',
			lineHeight: 1.35,
			width: '100%',
		}}
	>
		{value}
	</RowTypography>
);

const getPercentageCellClass = (
	value: number | null,
	isTotal = false,
) => {
	let cellClass = 'domain-na-cell';

	if (value !== null) {
		if (value >= 95) cellClass = 'domain-green-cell';
		else if (value >= 80) cellClass = 'domain-light-green-cell';
		else if (value >= 65) cellClass = 'domain-yellow-cell';
		else if (value >= 40) cellClass = 'domain-orange-cell';
		else cellClass = 'domain-red-cell';
	}

	return isTotal
		? `${cellClass} domain-total-cell`
		: cellClass;
};

const renderPercentage = (
	value: number | null,
	isTotal = false,
) => {
	if (value === null) {
		return renderTableValue('N/A', isTotal ? 700 : 500);
	}

	const formattedValue = Number.isInteger(value)
		? `${value}%`
		: `${value.toFixed(2)}%`;

	return renderTableValue(formattedValue, isTotal ? 700 : 500);
};

const headerRows: MultiLevelHeaderCell[][] = [
	[
		{
			key: 'criteria-group',
			label: 'Criteria',
			colSpan: 3,
			backgroundColor: HEADER_COLORS.criteria,
		},
		{
			key: 'year-2026-group',
			label: '2026',
			colSpan: 7,
			backgroundColor: HEADER_COLORS.year2026,
		},
		{
			key: 'year-2027-group',
			label: '2027',
			colSpan: 7,
			backgroundColor: HEADER_COLORS.year2027,
		},
	],
	[
		{
			key: 'category',
			label: 'Category',
			backgroundColor: HEADER_COLORS.criteria,
			minWidth: 80,
		},
		{
			key: 'subcategory',
			label: 'Subcategory',
			backgroundColor: HEADER_COLORS.criteria,
			minWidth: 120,
		},
		{
			key: 'geographicalUsage',
			label: (
				<>
					Geographical
					<br />
					Usage
				</>
			),
			backgroundColor: HEADER_COLORS.criteria,
			minWidth: 140,
		},
		{
			key: 'total2026',
			label: '2026 Total',
			backgroundColor: HEADER_COLORS.year2026,
			minWidth: 80,
		},
		{
			key: 'usBev2026',
			label: 'US Bev',
			backgroundColor: HEADER_COLORS.year2026,
			minWidth: 75,
		},
		{
			key: 'usFoods2026',
			label: 'US Foods',
			backgroundColor: HEADER_COLORS.year2026,
			minWidth: 75,
		},
		{
			key: 'latamFoods2026',
			label: 'LATAM Foods',
			backgroundColor: HEADER_COLORS.year2026,
			minWidth: 75,
		},
		{
			key: 'emea2026',
			label: 'EMEA',
			backgroundColor: HEADER_COLORS.year2026,
			minWidth: 75,
		},
		{
			key: 'apacFoods2026',
			label: 'APAC Foods',
			backgroundColor: HEADER_COLORS.year2026,
			minWidth: 75,
		},
		{
			key: 'corporate2026',
			label: 'Corporate',
			backgroundColor: HEADER_COLORS.year2026,
			minWidth: 80,
		},
		{
			key: 'total2027',
			label: '2027 Total',
			backgroundColor: HEADER_COLORS.year2027,
			minWidth: 80,
		},
		{
			key: 'usBev2027',
			label: 'US Bev',
			backgroundColor: HEADER_COLORS.year2027,
			minWidth: 75,
		},
		{
			key: 'usFoods2027',
			label: 'US Foods',
			backgroundColor: HEADER_COLORS.year2027,
			minWidth: 75,
		},
		{
			key: 'latamFoods2027',
			label: 'LATAM Foods',
			backgroundColor: HEADER_COLORS.year2027,
			minWidth: 75,
		},
		{
			key: 'emea2027',
			label: 'EMEA',
			backgroundColor: HEADER_COLORS.year2027,
			minWidth: 75,
		},
		{
			key: 'apacFoods2027',
			label: 'APAC Foods',
			backgroundColor: HEADER_COLORS.year2027,
			minWidth: 75,
		},
		{
			key: 'corporate2027',
			label: 'Corporate',
			backgroundColor: HEADER_COLORS.year2027,
			minWidth: 80,
		},
	],
];

const columns: MultiLevelTableColumn<CostAllocationRow>[] = [
	{
		key: 'category',
		width: 100,
		minWidth: 100,
		mergeRows: true,
	},
	{
		key: 'subcategory',
		width: 160,
		minWidth: 160,
	},
	{
		key: 'geographicalUsage',
		width: 140,
		minWidth: 140,
	},
	{
		key: 'total2026',
		minWidth: 80,
		fontWeight: 700,
		render: formatCurrency,
	},
	{
		key: 'usBev2026',
		minWidth: 75,
		render: formatCurrency,
	},
	{
		key: 'usFoods2026',
		minWidth: 75,
		render: formatCurrency,
	},
	{
		key: 'latamFoods2026',
		minWidth: 75,
		render: formatCurrency,
	},
	{
		key: 'emea2026',
		minWidth: 75,
		render: formatCurrency,
	},
	{
		key: 'apacFoods2026',
		minWidth: 75,
		render: formatCurrency,
	},
	{
		key: 'corporate2026',
		minWidth: 80,
		fontWeight: 700,
		render: formatCurrency,
	},
	{
		key: 'total2027',
		minWidth: 80,
		fontWeight: 700,
		render: formatCurrency,
	},
	{
		key: 'usBev2027',
		minWidth: 75,
		render: formatCurrency,
	},
	{
		key: 'usFoods2027',
		minWidth: 75,
		render: formatCurrency,
	},
	{
		key: 'latamFoods2027',
		minWidth: 75,
		render: formatCurrency,
	},
	{
		key: 'emea2027',
		minWidth: 75,
		render: formatCurrency,
	},
	{
		key: 'apacFoods2027',
		minWidth: 75,
		render: formatCurrency,
	},
	{
		key: 'corporate2027',
		minWidth: 80,
		fontWeight: 700,
		render: formatCurrency,
	},
];

const getDomainCoverageColumns = (): Array<Column<DomainCoverageRow>> => {
		const percentageColumn = (
		key: keyof DomainCoverageRow,
		name: string,
		minWidth = 70,
		width = 70,
	): Column<DomainCoverageRow> => ({
		key: String(key),
		name,
		resizable: true,
		minWidth,
		width,
		cellClass: (row) =>
			getPercentageCellClass(
				row[key] as number | null,
				Boolean(row.isTotal),
			),
		formatter: ({ row }) =>
			renderPercentage(
				row[key] as number | null,
				Boolean(row.isTotal),
			),
	});

	return [
		{ key: 'domain', name: 'DOMAIN', resizable: true, minWidth: 210, frozen: true, cellClass: (row) => row.isTotal ? 'domain-total-name-cell' : undefined, formatter: ({ row }) => renderMultilineTableValue(row.domain, row.isTotal ? 700 : 500) },
		percentageColumn('unitedStates', 'UNITED STATES'),
		percentageColumn('canada', 'CANADA'),
		percentageColumn('unitedKingdom', 'UNITED KINGDOM'),
		percentageColumn('france', 'FRANCE'),
		percentageColumn('netherlands', 'NETHERLANDS'),
		percentageColumn('spain', 'SPAIN'),
		percentageColumn('russia', 'RUSSIA'),
		percentageColumn('turkey', 'TURKEY'),
		percentageColumn('poland', 'POLAND'),
		percentageColumn('mexico', 'MEXICO'),
		percentageColumn('brazil', 'BRAZIL'),
		percentageColumn('southAfrica', 'SOUTH AFRICA'),
		percentageColumn('egypt', 'EGYPT'),
		percentageColumn('india', 'INDIA'),
		percentageColumn('saudiArabia', 'SAUDI ARABIA'),
		percentageColumn('unitedArabEmirates', 'UNITED ARAB EMIRATES'),
		percentageColumn('china', 'CHINA'),
		percentageColumn('anz', 'ANZ'),
		percentageColumn('total', 'TOTAL'),
	];
};

const getRetirementPlanColumns = (): Array<Column<RetirementPlanRow>> => [
	{ key: 'group', name: 'GROUP', resizable: true, minWidth: 180, frozen: true, cellClass: (row) => row.isTotal ? 'retirement-total-cell' : undefined, formatter: ({ row }) => renderMultilineTableValue(row.group, row.isTotal ? 700 : 500) },
	{ key: 'application', name: 'APPLICATION', resizable: true, minWidth: 280, frozen: true, cellClass: (row) => row.isTotal ? 'retirement-total-cell' : undefined, formatter: ({ row }) => renderMultilineTableValue(row.application, row.isTotal ? 700 : 500) },
	{ key: 'year2025', name: '2025', resizable: true, minWidth: 70, cellClass: (row) => row.isTotal ? 'retirement-total-cell' : undefined, formatter: ({ row }) => renderTableValue(row.year2025, row.isTotal ? 700 : 500) },
	{ key: 'year2026', name: '2026', resizable: true, minWidth: 70, cellClass: (row) => row.isTotal ? 'retirement-total-cell' : undefined, formatter: ({ row }) => renderTableValue(row.year2026, row.isTotal ? 700 : 500) },
	{ key: 'year2027', name: '2027', resizable: true, minWidth: 70, cellClass: (row) => row.isTotal ? 'retirement-total-cell' : undefined, formatter: ({ row }) => renderTableValue(row.year2027, row.isTotal ? 700 : 500) },
	{ key: 'year2028', name: '2028', resizable: true, minWidth: 70, cellClass: (row) => row.isTotal ? 'retirement-total-cell' : undefined, formatter: ({ row }) => renderTableValue(row.year2028, row.isTotal ? 700 : 500) },
	{ key: 'total', name: 'TOTAL ($MM)', resizable: true, minWidth: 70, cellClass: (row) => row.isTotal ? 'retirement-total-cell' : undefined, formatter: ({ row }) => renderTableValue(row.total, 700) },
	{ key: 'owner', name: 'OWNER', resizable: true, minWidth: 100, cellClass: (row) => row.isTotal ? 'retirement-empty-total-cell' : undefined, formatter: ({ row }) => renderTableValue(row.owner) },
	{ key: 'status', name: 'STATUS', resizable: true, minWidth: 600, cellClass: (row) => row.isTotal ? 'retirement-empty-total-cell' : 'retirement-status-cell', formatter: ({ row }) => renderMultilineTableValue(row.status) },
];

const EDFCostOverview = () => {
	const theme = useTheme();
	const colors = theme.palette as any;
	const [costAllocationData, setCostAllocationData] = useState<CostAllocationRow[]>([]);
	const [domainCoverageData, setDomainCoverageData] = useState<DomainCoverageRow[]>([]);
	const [retirementPlanData, setRetirementPlanData] = useState<RetirementPlanRow[]>([]);
	const [catalogingData, setCatalogingData] = useState<KpiItem[]>([]);
	const [heatmapData, setHeatmapData] = useState<HeatmapRow[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const domainCoverageColumns = getDomainCoverageColumns();
	const retirementPlanColumns = getRetirementPlanColumns();

	const domainCoverageDefaultColumns = domainCoverageColumns.map(
		(column) => column.name as string,
	);
	const retirementPlanDefaultColumns = retirementPlanColumns.map(
		(column) => column.name as string,
	);

	useEffect(() => {
		const fetchEdfData = async () => {
			setIsLoading(true);

			const [
				benefitsResult,
				catalogingResult,
				costOverviewResult,
				heatMapResult,
				priorityMarketsResult,
			] = await Promise.allSettled([
				getEdfBenefitsTable(),
				getEdfCatalogingTable(),
				getEdfCostOverviewTable(),
				getEdfHeatMapTable(),
				getEdfPriorityMarketsTable(),
			]);

			if (benefitsResult.status === 'fulfilled') {
				setRetirementPlanData(
					benefitsResult.value?.data?.rows ?? [],
				);
			} else {
				console.error(
					'Failed to fetch EDF Benefits:',
					benefitsResult.reason,
				);
			}

			if (catalogingResult.status === 'fulfilled') {
				setCatalogingData(
					catalogingResult.value?.data?.rows ?? [],
				);
			} else {
				console.error(
					'Failed to fetch EDF Cataloging:',
					catalogingResult.reason,
				);
			}

			if (costOverviewResult.status === 'fulfilled') {
				setCostAllocationData(
					costOverviewResult.value?.data?.rows ?? [],
				);
			} else {
				console.error(
					'Failed to fetch EDF Cost Overview:',
					costOverviewResult.reason,
				);
			}

			if (heatMapResult.status === 'fulfilled') {
				setHeatmapData(
					heatMapResult.value?.data?.rows ?? [],
				);
			} else {
				console.error(
					'Failed to fetch EDF Heat Map:',
					heatMapResult.reason,
				);
			}

			if (priorityMarketsResult.status === 'fulfilled') {
				setDomainCoverageData(
					priorityMarketsResult.value?.data?.rows ?? [],
				);
			} else {
				console.error(
					'Failed to fetch EDF Priority Markets:',
					priorityMarketsResult.reason,
				);
			}

			setIsLoading(false);
		};

		fetchEdfData();
	}, []);

	return (
		<PageWrapper>
			<GlobalStyles
				styles={{
					'.domain-green-cell': { backgroundColor: '#74C67A !important' },
					'.domain-light-green-cell': { backgroundColor: '#BCE37E !important' },
					'.domain-yellow-cell': { backgroundColor: '#FDE576 !important' },
					'.domain-orange-cell': { backgroundColor: '#F9B273 !important' },
					'.domain-red-cell': { backgroundColor: '#EE7676 !important' },
					'.domain-na-cell': { backgroundColor: '#BDBDBD !important' },
					'.domain-total-cell': { fontWeight: '700 !important' },
					'.domain-total-name-cell': { fontWeight: '700 !important' },
					'.retirement-total-cell': {
                        backgroundColor: '#003C63 !important',

                        '&, & *': {
                            color: '#FFFFFF !important',
                        },

                        fontWeight: '700 !important',
                    },
					'.retirement-empty-total-cell': {
						backgroundColor: '#FFFFFF !important',
					},
                    '.retirement-status-cell': {
                        whiteSpace: 'normal !important',
                        overflow: 'visible !important',
                        alignItems: 'flex-start !important',
                        paddingTop: '8px !important',
                        paddingBottom: '8px !important',
                    },
					'.domain-coverage-grid, .retirement-plan-grid': {
						'&, & *': {
							fontSize: '12px !important',
						},
					},
				}}
			/>

			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget?.text?.title ?? '#003771',
					fontWeight: 600,
				}}
			>
				EDF Cost Overview
			</Typography>

			{isLoading ? (
				<LoaderComponent pt={20} />
			) : (
				<MultiLevelMergedTable
					headerRows={headerRows}
					columns={columns}
					data={costAllocationData}
					getRowKey={(row) => row.id}
					isTotalRow={(row) => Boolean(row.isTotal)}
					isSubtotalRow={(row) => Boolean(row.isSubtotal)}
					bodyBackgroundColor="#EAF3FA"
					subtotalRowBackgroundColor="#909090"
					totalRowBackgroundColor="#DCEAF6"
					bodyTextColor="#000"
					borderColor="#FFFFFF"
					height={708}
					stickyHeader
					frozenColumnCount={3}
				/>
			)}

			<Divider sx={{ my: 3 }} />	

			{/* ─── Section 2: EDF Cataloging & Data Quality ────────────────────── */}
			<EDFCataloging
				kpiCardData={catalogingData}
				heatmapTableData={heatmapData}
				isLoading={isLoading}
			/>

			<Divider sx={{ my: 3 }} />
			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget?.text?.title ?? '#003771',
					fontWeight: 600,
				}}
			>
				EDF Cataloging & data quality | Priority Markets
			</Typography>

			{isLoading ? (
				<LoaderComponent pt={20} />
			) : (
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Box className="domain-coverage-grid" sx={{ width: '100%', '&, & *': { fontSize: '12px !important' } }}>
							<CustomizedDataGrid
								tableColumns={domainCoverageColumns}
								tableData={domainCoverageData}
								tableType="edfDomainCoverage"
								defaultColumnNames={domainCoverageDefaultColumns}
								listLength={domainCoverageData.length}
								isFetching={false}
								isRenderPagination={false}
								isShowMoreButton={false}
								autoSetDraggedColumns
								isLighterTheme
								isDateRangeFilter={false}
								isDisabled={false}
								showExport={false}
							/>
						</Box>
					</Grid>
				</Grid>
			)}

			<Divider sx={{ my: 3 }} />
			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget?.text?.title ?? '#003771',
					fontWeight: 600,
				}}
			>
				EDF Benefits | Cost Savings
			</Typography>

			{isLoading ? (
				<LoaderComponent pt={20} />
			) : (
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Box className="retirement-plan-grid" sx={{ width: '100%', '&, & *': { fontSize: '12px !important' } }}>
							<CustomizedDataGrid
								tableColumns={retirementPlanColumns}
								tableData={retirementPlanData}
								tableType="edfRetirementPlan"
								defaultColumnNames={retirementPlanDefaultColumns}
								listLength={retirementPlanData.length}
								isFetching={false}
								isRenderPagination={false}
								isShowMoreButton={false}
								autoSetDraggedColumns
								isLighterTheme
								isDateRangeFilter={false}
								isDisabled={false}
								showExport={false}
							/>
						</Box>
					</Grid>
				</Grid>
			)}
		</PageWrapper>
	);
};

export default EDFCostOverview;